import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ChevronLeft, ChevronRight, Plus, Search, Trash2, UserPen, Users } from 'lucide-react';

function UserManagePage() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('ALL');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [form, setForm] = useState({ id: null, name: '', email: '', password: '', role: 'USER', attempts: 0, voice_attempts: 0, examination_assist_attempts: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(false);

  const fetchData = async () => {
    const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/datatable/all-users`, {
      params: { start: page * limit, length: limit, search, role },
      withCredentials: true,
    });
    setData(res.data.data);
    setTotal(res.data.recordsFiltered);
  };

  useEffect(() => {
    fetchData();
  }, [page, search, role, limit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/admin/users`, form, { withCredentials: true });
      setShowModal(false);
      fetchData();
      resetForm();
      Swal.fire('Berhasil', 'Data berhasil disimpan', 'success');
    } catch (err) {
      Swal.fire('Oops!', err.response?.data?.message || "Silakan coba lagi.", 'error');
    }
  };

  const resetForm = () => {
    setForm({ id: null, name: '', email: '', password: '', role: 'USER', attempts: 0, voice_attempts: 0, examination_assist_attempts: 0 });
    setEditing(false);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const editForm = async (user) => {
    setForm({ ...user, password: '' });
    setEditing(true);
    setShowModal(true);
  };

  const deleteItem = async (id) => {
    const result = await Swal.fire({
      title: 'Yakin ingin menghapus?',
      text: 'Data akan dihapus secara permanen.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, hapus!',
      cancelButtonText: 'Batal'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${id}`, { withCredentials: true });
        fetchData();
        Swal.fire('Berhasil', 'Data berhasil dihapus', 'success');
      } catch (err) {
        Swal.fire('Oops!', err.response?.data?.message || "Silakan coba lagi.", 'error');
      }
    }
  };

  return (
    <div className="container-fluid px-0">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-slate-800">
          <Users size={18} />
          <h4 className="mb-0 text-lg font-semibold">Daftar Pengguna</h4>
        </div>

        <div className="mb-4 d-flex flex-wrap align-items-end justify-content-between gap-3">
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Tampilkan</span>
              <select className="form-select form-select-sm rounded-lg border-slate-300" style={{ width: '88px' }} value={limit} onChange={(e) => { setPage(0); setLimit(Number(e.target.value)); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="small text-muted">data</span>
            </div>
            <div style={{ minWidth: '180px' }}>
              <select className="form-select rounded-lg border-slate-300" value={role} onChange={(e) => { setPage(0); setRole(e.target.value); }}>
                <option value="ALL">Semua Role</option>
                <option value="ADMIN">ADMIN</option>
                <option value="USER">USER</option>
              </select>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-end gap-3">
            <div className="min-w-[280px] max-w-[360px] flex-1">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search size={16} /></span>
                <input type="text" className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20" placeholder="Cari nama atau email..." value={search} onChange={(e) => { setPage(0); setSearch(e.target.value); }} />
              </div>
            </div>
            <button className="btn btn-dark d-inline-flex align-items-center gap-2" onClick={openCreateModal}><Plus size={16} /> Tambah Data Baru</button>
          </div>
        </div>

        <div className='table-responsive rounded-xl border border-slate-300 bg-white'>
          <table className="table table-hover table-striped table-bordered table-sm mb-0">
            <thead>
              <tr>
                <th className='bg-light'>Email</th>
                <th className='bg-light'>Nama</th>
                <th className='bg-light text-center' width="7%">Role</th>
                <th className='bg-light' width="15%">Last Login</th>
                <th className='bg-light text-center' width="10%">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.map(user => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.name}</td>
                  <td className='text-center fw-bold'>{user.role}</td>
                  <td>{user.last_login ? new Date(user.last_login).toLocaleString() : '-'}</td>
                  <td className='text-center'>
                    <button className="btn btn-outline-dark btn-sm me-1" onClick={() => editForm(user)}><UserPen size={14} /></button>
                    <button className="btn btn-outline-dark btn-sm" onClick={() => deleteItem(user.id)}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 d-flex justify-content-start align-items-center gap-3 text-sm text-slate-500">
          <small>Total: {total}</small>
          <div className="d-inline-flex align-items-center gap-2 rounded-pill border border-slate-200 bg-white px-2 py-1 shadow-sm">
            <button className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Halaman sebelumnya"><ChevronLeft size={18} /></button>
            <span className="px-2 small text-muted">{page + 1} / {Math.max(1, Math.ceil(total / limit))}</span>
            <button className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center" disabled={(page + 1) * limit >= total} onClick={() => setPage(page + 1)} aria-label="Halaman berikutnya"><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">{editing ? 'Edit' : 'Tambah'} Pengguna</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>
                <div className="modal-body row g-2">
                  <div className="col-12">
                    <label>Nama</label>
                    <input type="text" className="form-control" name="name" value={form.name} onChange={handleChange} required />
                  </div>
                  <div className="col-12">
                    <label>Email</label>
                    <input type="email" className="form-control" name="email" value={form.email} onChange={handleChange} required />
                  </div>
                  <div className="col-12">
                    <label>
                      Password {editing && <sub>Lewati jika tidak diubah</sub>}
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      required={!editing}
                    />
                  </div>
                  <div className="col-12">
                    <label>Role</label>
                    <select className="form-control" name="role" value={form.role} onChange={handleChange} required>
                      <option value="USER">USER</option>
                      <option value="ADMIN">ADMIN</option>
                    </select>
                  </div>

                  {(form.role === 'USER' && <>

                    {editing && <>

                      <div className="col-6">
                        <label>Attempts</label>
                        <input type="number" min="0" className="form-control" name="attempts" value={form.attempts} onChange={handleChange} />
                      </div>
                      <div className="col-6">
                        <label>Voice Attempts</label>
                        <input type="number" min="0" className="form-control" name="voice_attempts" value={form.voice_attempts} onChange={handleChange} />
                      </div>
                      <div className="col-6">
                        <label>Examination Assistant Attempts</label>
                        <input type="number" min="0" className="form-control" name="examination_assist_attempts" value={form.examination_assist_attempts} onChange={handleChange} />
                      </div>

                    </>}

                  </>)}

                </div>
                <div className="modal-footer">
                  <button type="submit" className="btn btn-dark">{editing ? 'Update' : 'Simpan'}</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Tutup</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagePage;
